// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SystemDocumentType } from '@interfaces/document.interfaces';
import { AppEvent } from '@interfaces/interfaces';
import { User, UserLogonState } from '@interfaces/user.interfaces';
import { EnrollmentService } from '@services/enrollment.service';
import { UserService } from '@services/user.service';
import { finalize, mergeMap, Observable, of } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';
import { SuccessModalComponent } from 'src/app/modals/success-modal/success-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { ErrorService } from '@services/error.service';
import { catchError } from 'rxjs';
import { fullRoute as AdminGettingStartedRoute } from '../../../shared/admin-getting-started/admin-getting-started.component';
import { DealershipService } from '../../../services/dealership.service';
import { SettingsService } from '@services/settings.service';
import { AuthService } from '../../services/auth.service';
import { LoginService } from '../../services/login.service';

@Component({
    selector: 'app-initial-user-password',
    templateUrl: './initial-user-password.component.html',
    styleUrls: ['./initial-user-password.component.scss'],
})
export class InitialUserPasswordComponent implements OnInit {
    @Output() signal: EventEmitter<AppEvent> = new EventEmitter();
    @Input() user: User;
    @Input() dealerId: string;
    @Input() dealerName: string;
    @Input() dealerCode: string;
    @Input() token: string | null;
    @Input() jacketId: string | null;
    @Input() needsNewPassword: boolean;
    @Input() checkedSystemDocuments: SystemDocumentType[];
    @Input() email: string;
    @Input() firstName: string;
    @Input() lastName: string;
    @Input() phone: string;
    @Input() jobTItle: string;
    @Input() carryoverPassword: string;
    validPassword = '';
    isLoading = true;
    isComponentAlive = true;
    tenantSupportEmail = '';
    tenantSupportPhone = '';

    constructor(
        private readonly authService: AuthService,
        private readonly enrollmentService: EnrollmentService,
        private readonly dialog: MatDialog,
        private readonly errorService: ErrorService,
        private readonly loginService: LoginService,
        private readonly userService: UserService,
        private readonly dealershipService: DealershipService,
        private readonly settingsService: SettingsService
    ) {}

    ngOnInit() {
        const updateOemSettings = async (oemCode) => {
            const { supportEmail, supportPhone } = await this.settingsService.getOemSettings(oemCode);
            this.tenantSupportEmail = supportEmail;
            this.tenantSupportPhone = supportPhone;
        };
        this.dealershipService.dealer$.pipe(takeWhile(() => this.isComponentAlive)).subscribe(async (dealer) => {
            await updateOemSettings(dealer.oemCode);
        });
        this.settingsService.oemCodeEvent().subscribe(updateOemSettings);

        if (!this.needsNewPassword) {
            // this is a returning user, who does not need to set a password.
            // so immediately enroll the dealer and continue.
            this.validPassword = this.carryoverPassword;
            this.enroll();
        } else {
            this.isLoading = false;
        }
    }

    formValid() {
        return !!this.validPassword;
    }

    enroll() {
        this.isLoading = true;

        this.enrollmentService
            .enroll(this.token, this.jacketId, this.dealerCode, this.email, this.firstName, this.lastName, this.phone, this.jobTItle)
            .subscribe((data) => {
                const { user, temporaryToken } = data;

                if (user) {
                    this.user = user;

                    this.signal.emit({
                        context: 'data',
                        data: {
                            currentUser: user,
                        },
                    });

                    if (this.needsNewPassword) {
                        // save new password
                        this.save();
                    } else {
                        // existing user, skip straight to login
                        this.login(user.email, this.validPassword);
                    }
                }
            });
    }

    private save() {
        if (!this.validPassword) {
            this.dialog.open(SuccessModalComponent, {
                width: '700px',
                data: {
                    btnText: 'Continue',
                    title: 'Password Required to Continue',
                    content: 'For security purposes, you must enter a password meeting all requirements. Click Continue to return to the screen to enter it.',
                },
            });
        } else {
            this.isLoading = true;

            this.enrollmentService.setUserPassword(this.token, this.jacketId, this.dealerCode, this.user.logonId, this.validPassword).subscribe((success) => {
                if (success) {
                    this.login(this.user.email, this.validPassword);
                } else {
                    this.errorService.showError({
                        statusCode: 500,

                        message: `Please try again and if you continue to experience issues, please contact us. 
                              
                              EZ DOCS Support
                              Phone: ${this.tenantSupportPhone}
                              Email: ${this.tenantSupportEmail}`,
                    });
                }
            });
        }
    }

    enableMfaForUser() {
        return this.userService
            .adminUpdateUser$(
                this.user.userId,
                this.user.email,
                this.user.firstName,
                this.user.lastName,
                this.user.phone,
                this.user.systemAdministratorAtDealerId,
                this.user.middleName,
                this.user.dealerId,
                this.user.externalUserId,
                true,
                this.user.active
            )
            .pipe(
                catchError((error) => {
                    this.errorService.showError('You were successfully logged in, but we were unable to turn on multi factor authentication at this time.');
                    throw error;
                })
            );
    }

    login(email: string, password: string) {
        this.signal.emit({
            context: 'login',
            data: {
                email: email,
                password: password,
                onSuccess: (): Promise<AppEvent[]> =>
                    new Promise<AppEvent[]>((resolve, _) => {
                        // now that we're no longer in [NEW_PASSWORD_REQUIRED] status, we can perform authenticated calls.
                        // ...such as checking the System Documents that were shown on the InitialDealerSetup component.

                        this.enableMfaForUser().subscribe(() => {
                            const user = this.userService.user;
                            if (user) {
                                this.loginService.logLoginState(user.userId, user.logonId, UserLogonState.PasswordSuccessMFAPending).subscribe();
                            }

                            of(...this.checkedSystemDocuments)
                                .pipe(
                                    mergeMap((doc) => this.userService.updateRequireSystemDoc(doc)),
                                    finalize(() => {
                                        // this component is no longer visible, so it's event emitter is no longer connected to anything :(
                                        // returning a list of events we wish we could emit, allows the entrypoint to call them for us.
                                        // if you can get this.signal.emit() to work directly here instead, please do...
                                        return this.onComplete().subscribe(resolve);
                                    })
                                )
                                .subscribe(() => {
                                    this.isLoading = false;
                                    //update the user in the user service to reflect that they have accepted all system documents
                                    //we want to make sure the user state shows system documents accepted before the route guard checks
                                    this.userService.setUser({
                                        ...this.userService.user,
                                        needToAcceptSystemDocument: false,
                                    }); //defensive protection against race condition
                                });
                        });
                    }),
                onFailure: () => {
                    // hide the loading modal, let them reenter their password and try again
                    this.isLoading = false;
                },
                onMfaStart: () => {
                    // hide the loading modal, the EntrypointComponent handles mfa
                    this.isLoading = false;
                },
            },
        });
    }

    // this is the first place where both a new and returning user will be logged in and able to make authenticated calls.
    onComplete(): Observable<AppEvent[]> {
        // send welcome email/text
        return this.enrollmentService.sendEnrollmentNotification(this.dealerId, this.user.firstName, this.user.lastName, this.user.email, this.user.phone).pipe(
            // mark logon as successful
            map(() => this.loginService.logLoginState(this.user.userId, this.user.logonId, UserLogonState.LogonProcessComplete)),
            map(() => {
                // redirectUrl is set if this is from the document jacket enrollment flow
                const redirectTo = this.authService.redirectUrl ?? AdminGettingStartedRoute;
                return [{ context: 'goToRoute', data: [redirectTo] }];
            })
        );
    }

    cancel() {
        this.signal.emit({ context: 'cancel', data: null });
    }

    handlePasswordValid = (passwordEvent: string) => {
        this.validPassword = passwordEvent;
    };

    handlePasswordInvalid = () => {
        this.validPassword = null;
    };
}
